'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { ProductStockStatus } from '@/fo/api/magento';
import { UiKitCheckboxBase } from '@/fo/ui/kit/checkbox/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiProductButtonBuy, ButtonBuy } from '@/fo/ui/product/button/buy';
// import { UiProductButtonSubscribe } from '@/fo/ui/product/button/subscribe';
import { UiProductCompareButton } from '@/fo/ui/product/compare/button';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import {
  Option,
  Label,
  StockStatus,
  ProductOverlay
} from '@/fo/util/map-product-data';

import { styles } from './UiProductTileBase.styles';

export interface UiProductTileBaseProps {
  ref?: React.RefObject<HTMLElement | null>;
  className?: string;
  isInCart?: boolean;
  isInWishlist?: boolean;
  isInCompare?: boolean;
  /**
   * Ссылка на страницу товара
   *
   * Кликабельные элементы внутри плитки должны иметь z-index выше, чем slots.link
   */
  link: string;
  /** Изображение товара */
  image: string;
  /** Название бренда */
  brand?: {
    color: string;
    name: string;
    url: string;
  };
  /** ID товара в mindbox */
  mindboxId: string;
  /** Статус наличия товара */
  stockStatus: StockStatus;
  /** Название товара */
  title: string;
  /** Описание товара */
  description: string;
  /** Название серии */
  series: string;
  /** Опции товара (объем, цвет и т.д.) */
  options?: Option[];
  /** Текущая цена */
  price: number | null;
  /** Старая цена */
  oldPrice?: number | null;
  /** Валюта */
  currency?: string;
  /** Рейтинг */
  rating?: number;
  /** Количество отзывов */
  reviewCount?: number;
  /** Средний рейтинг */
  bestRating?: number;
  /** Худший рейтинг */
  worstRating?: number;
  /** Количество баллов лояльности */
  bonuses?: number;
  bonusesMax?: number;
  /** Лейбл баллов лояльности */
  bonusesLabel?: string;
  /** Авторизован ли пользователь */
  isAuthorized?: boolean;
  /** Метки для товара (скидки, хиты и т.д.) */
  labels?: Label[];
  /** Перекрытия товара */
  overlays?: ProductOverlay[];
  /** Выбран ли товар (для чекбокса) */
  isSelected?: boolean;
  /** Обработчик клика по карточке */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Обработчик клика по опции */
  onOptionClick?: (option: Option) => void;
  /** Обработчик клика по корзине */
  onCartClick?: () => void;
  /** Обработчик клика по избранному */
  onWishlistClick?: () => void;
  /** Обработчик клика по сравнению */
  onCompareClick?: () => void;
  /** Обработчик клика по подписке на продукт */
  onSubscribeClick?: () => void;
  /** Обработчик изменения чекбокса */
  onSelect?: (selected: boolean) => void;
  /** Загрузка кнопки корзины */
  loadingCartButton?: boolean;
  /** Показывать опции */
  showOptions?: boolean;
  type?: 'base' | 'search';
  discount?: string | null;
}

export function UiProductTileBase({
  ref,
  className,
  isInCart = false,
  isInWishlist = false,
  isInCompare = false,
  link,
  image,
  // mindboxId,
  stockStatus,
  title,
  series,
  price,
  oldPrice,
  currency,
  labels,
  isSelected,
  onClick,
  onCartClick,
  onWishlistClick,
  onCompareClick,
  // onSubscribeClick,
  onSelect,
  loadingCartButton,
  overlays,
  type = 'base',
  discount
}: UiProductTileBaseProps) {
  const t = useTranslations();
  const slots = styles({
    wishlist: isInWishlist,
    type,
    compare: isInCompare,
    withSelect: Boolean(onSelect)
  });
  const isOutOfStock = stockStatus === ProductStockStatus.OutOfStock;
  const hasLabels = labels && labels.length > 0;
  const hasOverlays = overlays && overlays.length > 0;
  const isDesktop = useMediaQuery('xl');
  // Функция для получения schema.org availability значения
  const getAvailabilitySchema = (stockStatus: StockStatus) => {
    switch (stockStatus) {
      case ProductStockStatus.InStock:
        return 'https://schema.org/InStock';
      case ProductStockStatus.OutOfStock:
        return 'https://schema.org/OutOfStock';
      default:
        return 'https://schema.org/InStock';
    }
  };

  return (
    <article
      ref={ref}
      className={slots.base({ className })}
      onClick={onClick}
      itemScope
      itemType="https://schema.org/Product"
    >
      {/* Скрытые метаданные для микроразметки */}
      <meta
        itemProp="url"
        content={link}
      />
      <meta
        itemProp="brand"
        itemScope
        itemType="https://schema.org/Brand"
        content="Brand"
      />

      <div className={slots.imageContainer()}>
        <div
          className={slots.imageWrapper({
            className: isOutOfStock
              ? 'after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:rounded-[8px] after:bg-white after:opacity-50'
              : ''
          })}
        >
          <Image
            className={slots.image()}
            width={324}
            height={324}
            src={image}
            alt={title}
            itemProp="image"
          />
          <UiProductWishlistButton
            className={slots.wishlist()}
            isActive={isInWishlist}
            onClick={onWishlistClick}
          />
          <UiProductCompareButton
            className={slots.compare()}
            isActive={isInCompare}
            onClick={onCompareClick}
          />

          {onSelect && (
            <div className={slots.checkbox()}>
              <UiKitCheckboxBase
                checked={isSelected}
                onChange={onSelect}
              />
            </div>
          )}
          {!isDesktop && !isOutOfStock && (
            <ButtonBuy
              className={slots.button()}
              isInCart={isInCart}
              loading={loadingCartButton}
              onClick={onCartClick}
            />
          )}
        </div>
        {!onSelect && (hasLabels || hasOverlays || discount) && (
          <div className={slots.labels()}>
            {(hasLabels || hasOverlays) &&
              labels?.map((label, index) => (
                <UiKitLabelBase
                  key={index}
                  customColor={label.color}
                >
                  {label.name}
                </UiKitLabelBase>
              ))}
            {discount && <UiKitLabelBase>{discount}</UiKitLabelBase>}
            {overlays?.map((overlay) => (
              <div
                key={overlay.id}
                className={slots.productOverlay()}
                dangerouslySetInnerHTML={{ __html: overlay.catText }}
              />
            ))}
          </div>
        )}

        {isOutOfStock && (
          <UiKitLabelBase
            color="black"
            className={slots.outOfStockLabel()}
            type={type}
          >
            {t('Out of stock')}
          </UiKitLabelBase>
        )}
      </div>

      <div className={slots.content()}>
        <Link
          className={slots.link({
            className: isOutOfStock ? 'bg-white opacity-50' : ''
          })}
          href={link}
        />

        <div className={slots.bottom()}>
          <h2
            className={slots.info()}
            itemProp="name"
          >
            <span
              className={slots.title()}
              dangerouslySetInnerHTML={{ __html: `${series} ${title}` }}
            />
          </h2>

          <div className={slots.actions()}>
            {/* Микроразметка для предложения (цена, наличие) */}
            {(price !== null || isOutOfStock) && (
              <div
                itemProp="offers"
                itemScope
                itemType="https://schema.org/Offer"
                style={{ display: 'contents' }}
              >
                <meta
                  itemProp="availability"
                  content={getAvailabilitySchema(stockStatus)}
                />
                <meta
                  itemProp="priceCurrency"
                  content="RUB"
                />
                {price !== null && (
                  <meta
                    itemProp="price"
                    content={price.toString()}
                  />
                )}

                {/* {isOutOfStock && mindboxId && (
                  <UiProductButtonSubscribe
                    onClick={onSubscribeClick}
                    className={slots.actionButton()}
                  />
                )} */}

                {price !== null && (
                  <UiProductButtonBuy
                    isInCart={isInCart}
                    className={slots.actionButton()}
                    classNamePrice={slots.buttonBuyPrice()}
                    classNamePriceValue={slots.buttonBuyPriceValue()}
                    classNamePriceOldValue={slots.buttonBuyPriceOldValue()}
                    price={price}
                    currency={currency}
                    oldPrice={oldPrice}
                    onClick={onCartClick}
                    loading={loadingCartButton}
                    isOutOfStock={isOutOfStock}
                    variantBadge={onSelect ? 'simple' : 'detailed'}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
